import React, { useContext, useEffect, useState } from "react";
import {
  Badge,
  IconButton,
  Popover,
  Box,
  Typography,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Notifications,
  NotificationsActive,
  Login,
  DoneAll,
  Circle,
  Settings,
  CalendarMonth,
  CurrencyRupee,
  Build,
  Campaign,
  HowToVote,
  Forum,
  DynamicFeed,
  Event,
  Apartment,
  PersonPin,
} from "@mui/icons-material";
import { AuthContext } from "../AppContext/AuthContext";
import {
  getNotificationsInRealTime,
  markNotificationRead,
  markAllNotificationsRead,
} from "../Config/db/visitor.operations";
import {
  onForegroundMessage,
  requestNotificationPermission,
} from "../Config/db/notification.operations";
import { useNavigate } from "react-router-dom";

function timeAgo(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const secs = Math.floor((Date.now() - d.getTime()) / 1000);
  if (secs < 60) return "Just now";
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

const ICON_MAP = {
  visitor_checkin: <Login sx={{ fontSize: 20, color: "#4caf50" }} />,
  visitors: <PersonPin sx={{ fontSize: 20, color: "#f57c00" }} />,
  meetings: <CalendarMonth sx={{ fontSize: 20, color: "#1565c0" }} />,
  finance: <CurrencyRupee sx={{ fontSize: 20, color: "#43a047" }} />,
  maintenance: <Build sx={{ fontSize: 20, color: "#ff9800" }} />,
  notices: <Campaign sx={{ fontSize: 20, color: "#d32f2f" }} />,
  polls: <HowToVote sx={{ fontSize: 20, color: "#7b1fa2" }} />,
  chat: <Forum sx={{ fontSize: 20, color: "#0288d1" }} />,
  social: <DynamicFeed sx={{ fontSize: 20, color: "#1565c0" }} />,
  amenities: <Event sx={{ fontSize: 20, color: "#00838f" }} />,
  community: <Apartment sx={{ fontSize: 20, color: "#5c6bc0" }} />,
};

/**
 * NotificationBell — shows a bell icon with unread count badge.
 * Click to see recent notifications in a popover.
 */
export default function NotificationBell() {
  const { currentUser } = useContext(AuthContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [snack, setSnack] = useState(null);
  const navigate = useNavigate();

  // In-app notifications (Firestore real-time)
  useEffect(() => {
    if (!currentUser?.uid) return;
    const unsub = getNotificationsInRealTime(currentUser.uid, setNotifications);
    return () => unsub();
  }, [currentUser?.uid]);

  // Auto-request FCM permission on mount (only prompts once)
  useEffect(() => {
    if (!currentUser?.uid) return;
    requestNotificationPermission(currentUser.uid);
  }, [currentUser?.uid]);

  // Foreground push messages → show snackbar
  useEffect(() => {
    let unsub;
    onForegroundMessage((payload) => {
      const { title, body } = payload.notification || {};
      setSnack({ title: title || "Notification", body: body || "" });
    }).then((fn) => { unsub = fn; });
    return () => { if (unsub) unsub(); };
  }, []);

  const unreadCount = notifications.filter((n) => !n.read).length;
  const recent = notifications.slice(0, 20);

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleMarkAllRead = async () => {
    const unreadIds = notifications
      .filter((n) => !n.read)
      .map((n) => n.id);
    if (unreadIds.length > 0) await markAllNotificationsRead(unreadIds);
  };

  const handleRead = async (id) => {
    await markNotificationRead(id);
  };

  return (
    <>
      <IconButton color="inherit" onClick={handleOpen} sx={{ mr: 0.5 }}>
        <Badge badgeContent={unreadCount} color="error" max={9}>
          {unreadCount > 0 ? (
            <NotificationsActive />
          ) : (
            <Notifications />
          )}
        </Badge>
      </IconButton>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: { width: { xs: "min(340px, 92vw)", sm: 340 }, maxHeight: 420, borderRadius: 2 },
        }}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography fontWeight={700}>Notifications</Typography>
          {unreadCount > 0 && (
            <Button
              size="small"
              startIcon={<DoneAll sx={{ fontSize: 16 }} />}
              onClick={handleMarkAllRead}
              sx={{ textTransform: "none", fontSize: "0.75rem" }}
            >
              Mark all read
            </Button>
          )}
        </Box>
        <Divider />

        {recent.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Notifications sx={{ fontSize: 40, color: "#ccc", mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No notifications yet
            </Typography>
          </Box>
        ) : (
          <List sx={{ py: 0 }}>
            {recent.map((n) => (
              <ListItem
                key={n.id}
                onClick={() => !n.read && handleRead(n.id)}
                sx={{
                  cursor: !n.read ? "pointer" : "default",
                  bgcolor: n.read ? "transparent" : "#e3f2fd",
                  "&:hover": {
                    bgcolor: n.read ? "#f5f5f5" : "#bbdefb",
                  },
                  borderBottom: "1px solid #f0f0f0",
                  py: 1.5,
                  alignItems: "flex-start",
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                  {ICON_MAP[n.type] || (
                    <Circle sx={{ fontSize: 10, color: "#1565c0" }} />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      variant="body2"
                      fontWeight={n.read ? 400 : 700}
                    >
                      {n.title}
                    </Typography>
                  }
                  secondary={
                    <>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        component="span"
                      >
                        {n.body}
                      </Typography>
                      <br />
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        component="span"
                      >
                        {timeAgo(n.createdAt)}
                      </Typography>
                    </>
                  }
                />
                {!n.read && (
                  <Circle
                    sx={{ fontSize: 8, color: "#1565c0", mt: 1 }}
                  />
                )}
              </ListItem>
            ))}
          </List>
        )}

        {/* Settings link */}
        <Divider />
        <Box sx={{ p: 1.5, textAlign: "center" }}>
          <Button
            size="small"
            startIcon={<Settings sx={{ fontSize: 16 }} />}
            onClick={() => { handleClose(); navigate("/notification-settings"); }}
            sx={{ textTransform: "none", fontSize: "0.75rem" }}
          >
            Notification Settings
          </Button>
        </Box>
      </Popover>

      {/* Foreground push snackbar */}
      <Snackbar
        open={!!snack}
        autoHideDuration={5000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {snack ? (
          <Alert
            onClose={() => setSnack(null)}
            severity="info"
            variant="filled"
            sx={{ width: "100%", borderRadius: 2 }}
          >
            <Typography variant="body2" fontWeight={700}>
              {snack.title}
            </Typography>
            <Typography variant="caption">{snack.body}</Typography>
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
}
